import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import PageHeader from './components/PageHeader';
import DashboardLoader from './components/DashboardLoader';
import {
  productsAPI, servicesAPI, testimonialsAPI, teamAPI,
  careersAPI, faqsAPI, partnersAPI, statsAPI, howItWorksAPI,
  navigationAPI, companyValuesAPI, socialLinksAPI,
} from '../../services/api';
import './ContentOverview.css';

function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === 'string') return d;
        const loc = Array.isArray(d?.loc) ? d.loc.join('.') : '';
        return [loc, d?.msg].filter(Boolean).join(': ');
      })
      .filter(Boolean)
      .join('; ');
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || JSON.stringify(detail);
  }
  return err?.message || 'Unknown error';
}

const SECTION_CONFIG = {
  products: {
    api: productsAPI,
    title: 'Product',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
    fields: [
      { key: 'name', label: 'Product Name', required: true },
      { key: 'tagline', label: 'Tagline', required: true },
      { key: 'category', label: 'Category', required: true, type: 'combobox', optionsFrom: 'category', defaults: ['Web App', 'Mobile App', 'API', 'Platform', 'Tool', 'Library'] },
      { key: 'status', label: 'Status', required: true, type: 'select', options: ['live', 'development', 'planned'] },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'url', label: 'URL' },
      { key: 'color', label: 'Color', type: 'color' },
      { key: 'icon', label: 'Icon (emoji)' },
      { key: 'tags', label: 'Tags (comma-separated)' },
      { key: 'sort_order', label: 'Sort Order', type: 'number' },
    ],
    nameKey: 'name',
  },
  services: {
    api: servicesAPI,
    title: 'Service',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
    fields: [
      { key: 'title', label: 'Service Title', required: true },
      { key: 'description', label: 'Description', type: 'textarea', required: true },
      { key: 'icon', label: 'Icon' },
      { key: 'sort_order', label: 'Sort Order', type: 'number' },
    ],
    nameKey: 'title',
  },
  team: {
    api: teamAPI,
    title: 'Team Member',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    fields: [
      { key: 'name', label: 'Full Name', required: true },
      { key: 'role', label: 'Role', required: true },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'avatar', label: 'Avatar (emoji or initials)' },
      { key: 'sort_order', label: 'Sort Order', type: 'number' },
    ],
    nameKey: 'name',
  },
  testimonials: {
    api: testimonialsAPI,
    title: 'Testimonial',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
    fields: [
      { key: 'name', label: 'Client Name', required: true },
      { key: 'role', label: 'Role/Company', required: true },
      { key: 'location', label: 'Location' },
      { key: 'text', label: 'Testimonial', type: 'textarea', required: true },
      { key: 'rating', label: 'Rating', type: 'number', min: 1, max: 5 },
    ],
    nameKey: 'name',
  },
  careers: {
    api: careersAPI,
    title: 'Career',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
    fields: [
      { key: 'title', label: 'Job Title', required: true },
      { key: 'department', label: 'Department', required: true },
      { key: 'location', label: 'Location', required: true },
      { key: 'type', label: 'Type', required: true, type: 'select', options: ['Full-time', 'Part-time', 'Contract', 'Remote', 'Internship'] },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'is_active', label: 'Active', type: 'select', options: ['true', 'false'] },
    ],
    nameKey: 'title',
  },
  faqs: {
    api: faqsAPI,
    title: 'FAQ',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    fields: [
      { key: 'question', label: 'Question', required: true },
      { key: 'answer', label: 'Answer', type: 'textarea', required: true },
      { key: 'sort_order', label: 'Sort Order', type: 'number' },
    ],
    nameKey: 'question',
  },
  stats: {
    api: statsAPI,
    title: 'Stat',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>,
    fields: [
      { key: 'value', label: 'Value', required: true },
      { key: 'label', label: 'Label', required: true },
      { key: 'sort_order', label: 'Sort Order', type: 'number' },
    ],
    nameKey: 'label',
  },
  partners: {
    api: partnersAPI,
    title: 'Partner',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"/></svg>,
    fields: [
      { key: 'name', label: 'Partner Name', required: true },
      { key: 'short', label: 'Short Name', required: true },
      { key: 'sort_order', label: 'Sort Order', type: 'number' },
    ],
    nameKey: 'name',
  },
  'how-it-works': {
    api: howItWorksAPI,
    title: 'Step',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    fields: [
      { key: 'step', label: 'Step Number', type: 'number', required: true },
      { key: 'title', label: 'Title', required: true },
      { key: 'description', label: 'Description', type: 'textarea', required: true },
    ],
    nameKey: 'title',
  },
  'company-values': {
    api: companyValuesAPI,
    title: 'Company Value',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
    fields: [
      { key: 'title', label: 'Value Title', required: true },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'sort_order', label: 'Sort Order', type: 'number' },
    ],
    nameKey: 'title',
  },
  'social-links': {
    api: socialLinksAPI,
    title: 'Social Link',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
    fields: [
      { key: 'platform', label: 'Platform', required: true },
      { key: 'url', label: 'URL', required: true },
    ],
    nameKey: 'platform',
  },
  navigation: {
    api: navigationAPI,
    title: 'Navigation Item',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
    fields: [
      { key: 'label', label: 'Link Label', required: true },
      { key: 'path', label: 'URL Path', required: true },
      { key: 'sort_order', label: 'Sort Order', type: 'number' },
      { key: 'is_visible', label: 'Visible', type: 'select', options: ['true', 'false'] },
      { key: 'requires_auth', label: 'Requires Auth', type: 'select', options: ['true', 'false'] },
    ],
    nameKey: 'label',
  },
};

export default function ContentOverview() {
  const { section, id } = useParams();
  const navigate = useNavigate();
  const { isEditor } = useAuth();
  const { refresh } = useData();
  const config = SECTION_CONFIG[section];

  const [item, setItem] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [form, setForm] = useState({});
  const lastSavedForm = useRef({});
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const fetchItem = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    try {
      const res = await config.api.list();
      const items = res.data || [];
      setAllItems(items);
      const found = items.find((i) => String(i.id) === String(id));
      if (found) {
        setItem(found);
        const formInit = {};
        config.fields.forEach((f) => {
          const val = found[f.key];
          formInit[f.key] = val !== null && val !== undefined
            ? (Array.isArray(val) ? val.join(', ') : String(val))
            : '';
        });
        setForm(formInit);
        lastSavedForm.current = { ...formInit };
      } else {
        setItem(null);
      }
    } catch (err) {
      console.error('Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  }, [config, id]);

  useEffect(() => {
    fetchItem();
  }, [fetchItem]);

  if (!config) {
    return (
      <div className="co-not-found">
        <p>Section not found</p>
        <button onClick={() => navigate('/dashboard')} className="btn-action btn-action--outline">Back to Dashboard</button>
      </div>
    );
  }

  if (loading) {
    return <DashboardLoader text={`Loading ${config.title.toLowerCase()}...`} />;
  }

  if (!item) {
    return (
      <div className="co-not-found">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="8" y1="15" x2="16" y2="15"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
        <p>{config.title} not found</p>
        <button onClick={() => navigate(`/dashboard/${section}`)} className="btn-action btn-action--outline">Back to {section}</button>
      </div>
    );
  }

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete this ${config.title.toLowerCase()}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await config.api.delete(item.id);
      refresh();
      navigate(`/dashboard/${section}`);
    } catch (err) {
      alert('Delete failed: ' + formatApiError(err));
      setDeleting(false);
    }
  };

  const handleFieldSave = async (key, overrideValue) => {
    const newVal = overrideValue !== undefined ? overrideValue : form[key];
    if (newVal === lastSavedForm.current[key]) return;
    try {
      let value = newVal;
      if (key === 'tags' && typeof value === 'string') {
        value = value.split(',').map((t) => t.trim()).filter(Boolean);
      }
      if (key === 'rating') value = Number(value);
      if (key === 'step') value = Number(value);
      if (key === 'sort_order') value = Number(value);
      if (key === 'is_visible' || key === 'requires_auth' || key === 'is_active') value = value === 'true' || value === true;
      await config.api.patch(item.id, key, value);
      lastSavedForm.current[key] = newVal;
      refresh();
    } catch (err) {
      alert('Update failed: ' + formatApiError(err));
    }
  };

  const itemName = item[config.nameKey] || config.title;

  return (
    <div className="co-page">
      <PageHeader
        title={itemName}
        subtitle={`${config.title} Overview`}
        icon={config.icon}
        backTo={`/dashboard/${section}`}
        actions={
          <div className="co-header-actions">
            {isEditor && (
              <button className="btn-action btn-action--danger" onClick={handleDelete} disabled={deleting}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        }
      />

      {/* Overview Cards */}
      <div className="co-grid">
        {/* Main Info Card */}
        <div className="co-card co-card--main">
          <div className="co-card__header">
            <h3 className="co-card__title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Information
            </h3>
          </div>
          <div className="co-card__body">
            {config.fields.filter((f) => f.type !== 'textarea').map((field) => (
              <div key={field.key} className="co-field">
                <label className="co-field__label">
                  {field.label}
                  {field.required && <span className="co-field__required">*</span>}
                </label>
                {field.type === 'select' ? (
                  <div className="co-field__select-wrap">
                    <select
                      className="co-field__select"
                      value={form[field.key] || ''}
                      onChange={(e) => { handleChange(field.key, e.target.value); handleFieldSave(field.key, e.target.value); }}
                      disabled={!isEditor}
                    >
                      <option value="">Select...</option>
                      {field.options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                ) : field.type === 'combobox' ? (() => {
                  const dynamicVals = field.optionsFrom
                    ? [...new Set(allItems.map(it => it[field.optionsFrom]).filter(Boolean))]
                    : [];
                  const allOptions = [...new Set([...(field.defaults || []), ...dynamicVals])];
                  const listId = `co-combobox-${field.key}`;
                  return (
                    <div className="co-field__input-wrap">
                      <input
                        list={listId}
                        className="co-field__input"
                        value={form[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        onBlur={() => isEditor && handleFieldSave(field.key)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                        placeholder={`Type or select ${field.label.toLowerCase()}`}
                        disabled={!isEditor}
                      />
                      <datalist id={listId}>
                        {allOptions.map((opt) => (
                          <option key={opt} value={opt} />
                        ))}
                      </datalist>
                    </div>
                  );
                })()
                : field.type === 'color' ? (
                  <div className="co-field__input-wrap">
                    <div className="co-field__color-wrap">
                      <input
                        type="color"
                        className="co-field__color-picker"
                        value={form[field.key] || '#000000'}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        onBlur={() => isEditor && handleFieldSave(field.key)}
                        disabled={!isEditor}
                      />
                      <span className="co-field__color-value">{form[field.key] || '#000000'}</span>
                    </div>
                  </div>
                ) : (
                  <div className="co-field__input-wrap">
                    <input
                      className="co-field__input"
                      type={field.type === 'number' ? 'number' : 'text'}
                      value={form[field.key] || ''}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      onBlur={() => isEditor && handleFieldSave(field.key)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                      placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                      disabled={!isEditor}
                      min={field.min}
                      max={field.max}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Textarea fields — each gets its own card */}
        {config.fields.filter((f) => f.type === 'textarea').map((field) => (
          <div key={field.key} className="co-card co-card--full">
            <div className="co-card__header">
              <h3 className="co-card__title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="3" y2="18"/>
                </svg>
                {field.label}
              </h3>
            </div>
            <div className="co-card__body">
              <textarea
                className="co-field__textarea"
                value={form[field.key] || ''}
                onChange={(e) => handleChange(field.key, e.target.value)}
                onBlur={() => isEditor && handleFieldSave(field.key)}
                placeholder={`Enter ${field.label.toLowerCase()}...`}
                disabled={!isEditor}
                rows={6}
              />
            </div>
          </div>
        ))}

        {/* Metadata Card */}
        <div className="co-card co-card--meta">
          <div className="co-card__header">
            <h3 className="co-card__title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              Metadata
            </h3>
          </div>
          <div className="co-card__body">
            <div className="co-meta-row">
              <span className="co-meta-row__label">ID</span>
              <span className="co-meta-row__value co-meta-row__value--mono">{item.id}</span>
            </div>
            {item.created_at && (
              <div className="co-meta-row">
                <span className="co-meta-row__label">Created</span>
                <span className="co-meta-row__value">{new Date(item.created_at).toLocaleString()}</span>
              </div>
            )}
            {item.updated_at && (
              <div className="co-meta-row">
                <span className="co-meta-row__label">Updated</span>
                <span className="co-meta-row__value">{new Date(item.updated_at).toLocaleString()}</span>
              </div>
            )}
            {/* Any extra fields not in config.fields */}
            {Object.entries(item)
              .filter(([k]) => !config.fields.find((f) => f.key === k) && !['id', 'created_at', 'updated_at'].includes(k))
              .map(([key, val]) => (
                <div key={key} className="co-meta-row">
                  <span className="co-meta-row__label">{key.replace(/_/g, ' ')}</span>
                  <span className="co-meta-row__value">
                    {typeof val === 'object' ? JSON.stringify(val) : String(val ?? '—')}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
